"use client";

import { ConfirmDialog } from "@/components/ui/molecules/ConfirmDialog";
import { useRouter } from "next/navigation";
import { createContext, memo, type ReactNode, useCallback, useContext, useEffect, useId, useMemo, useRef, useState } from "react";

/** What the discard confirm says. */
export interface DiscardWords {
    title: string;
    description: string;
    confirmLabel: string;
    cancelLabel: string;
}

/** Leaving the page: a link, the browser's Back or Forward, search, Fluid, or the page's own way back. */
const LEAVE_WORDS: DiscardWords = { title: "Discard your changes?", description: "You have changes you haven't saved. Leaving discards them.", confirmLabel: "Discard", cancelLabel: "Keep editing" };
/** Closing a dialog, a sheet or a drill-in that holds the edits. */
export const CLOSE_WORDS: DiscardWords = { title: "Discard your changes?", description: "You have changes you haven't saved. Closing discards them.", confirmLabel: "Discard", cancelLabel: "Keep editing" };

interface LeaveGuard {
    setDirty: (id: string, dirty: boolean) => void;
    /** Asks, then goes on only on Discard. */
    confirmDiscard: (proceed: () => void, words?: DiscardWords) => void;
    /** Goes on at once while nothing on the page is unsaved; asks first while something is. */
    whenLeaving: (proceed: () => void) => void;
}

/** No guard: what a part rendered on its own sees (a component test), where nothing is held. RootLayout mounts the
 *  provider around every page of the app. */
const UNGUARDED: LeaveGuard = { setDirty: () => undefined, confirmDiscard: (proceed) => proceed(), whenLeaving: (proceed) => proceed() };

const LeaveGuardContext = createContext<LeaveGuard>(UNGUARDED);

/**
 * The one way a page keeps unsaved changes from being lost (2026-10-29): while anything registered with
 * {@link useLeaveGuard} is unsaved, every way out asks first, in the app's own confirm. A link inside the app is held
 * at its click, the browser's Back and Forward at the traversal (the Navigation API), search and Fluid through
 * {@link useGuardedRouter}, and the page's own way back through {@link useLeaveGuardActions}. A reload or a closed tab
 * can only be asked about by the browser's own prompt, so that is what it gets. Dialogs that hold edits ask through
 * {@link useGuardedClose}. A page that saves as it goes (the Design Studio) registers nothing: it has nothing to lose.
 */
export const LeaveGuardProvider = memo(function LeaveGuardProvider({ children }: { children: ReactNode }) {
    const dirtyIds = useRef(new Set<string>());
    const [anyDirty, setAnyDirty] = useState(false);
    const [asking, setAsking] = useState<{ proceed: () => void; words: DiscardWords; } | null>(null);
    // Set while the guard itself follows the held link or goes Back or Forward after a Discard, so that one is let
    // through.
    const following = useRef(false);
    const traversing = useRef(false);

    const setDirty = useCallback((id: string, dirty: boolean) => {
        if (dirty) {
            dirtyIds.current.add(id);
        } else {
            dirtyIds.current.delete(id);
        }
        setAnyDirty(dirtyIds.current.size > 0);
    }, []);
    const confirmDiscard = useCallback((proceed: () => void, words: DiscardWords = LEAVE_WORDS) => setAsking({ proceed, words }), []);
    const whenLeaving = useCallback((proceed: () => void) => {
        if (dirtyIds.current.size > 0) {
            setAsking({ proceed, words: LEAVE_WORDS });
        } else {
            proceed();
        }
    }, []);

    useEffect(() => {
        if (!anyDirty) {
            return;
        }
        // A link to another page of the app, followed in this tab: held before Next's own handler sees it, and on
        // Discard clicked again and let through, so the link does what it always does (its own onClick included). A
        // link that opens a new tab, downloads, or stays on this page (a fragment) leaves nothing behind, and one to
        // another site is the browser's to ask about on unload.
        const onClick = (event: MouseEvent) => {
            if (following.current) {
                following.current = false;
                return;
            }
            if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
                return;
            }
            const anchor = event.target instanceof Element ? event.target.closest("a[href]") : null;
            if (!(anchor instanceof HTMLAnchorElement) || anchor.hasAttribute("download") || (anchor.target !== "" && anchor.target !== "_self")) {
                return;
            }
            const url = new URL(anchor.href);
            if (url.origin !== window.location.origin || (url.pathname === window.location.pathname && url.search === window.location.search)) {
                return;
            }
            event.preventDefault();
            event.stopPropagation();
            setAsking({ proceed: () => {
                following.current = true;
                anchor.click();
            }, words: LEAVE_WORDS });
        };
        // Back and Forward: held before the page changes, then taken to the same entry on Discard.
        const navigation = window.navigation;
        const onNavigate = (event: NavigateEvent) => {
            if (event.navigationType !== "traverse") {
                return;
            }
            if (traversing.current) {
                traversing.current = false;
                return;
            }
            if (!event.cancelable) {
                return;
            }
            event.preventDefault();
            const key = event.destination.key;
            setAsking({ proceed: () => {
                traversing.current = true;
                navigation?.traverseTo(key);
            }, words: LEAVE_WORDS });
        };
        // A reload or a closed tab: only the browser may ask.
        const onBeforeUnload = (event: BeforeUnloadEvent) => {
            event.preventDefault();
            event.returnValue = "";
        };
        document.addEventListener("click", onClick, true);
        navigation?.addEventListener("navigate", onNavigate);
        window.addEventListener("beforeunload", onBeforeUnload);
        return () => {
            document.removeEventListener("click", onClick, true);
            navigation?.removeEventListener("navigate", onNavigate);
            window.removeEventListener("beforeunload", onBeforeUnload);
        };
    }, [anyDirty]);

    const guard = useMemo(() => ({ setDirty, confirmDiscard, whenLeaving }), [setDirty, confirmDiscard, whenLeaving]);

    return (
        <LeaveGuardContext.Provider value={guard}>
            {children}
            <ConfirmDialog
                open={asking !== null}
                onOpenChange={(open) => {
                    if (!open) {
                        setAsking(null);
                    }
                }}
                title={asking?.words.title ?? LEAVE_WORDS.title}
                description={asking?.words.description ?? LEAVE_WORDS.description}
                confirmLabel={asking?.words.confirmLabel ?? LEAVE_WORDS.confirmLabel}
                cancelLabel={asking?.words.cancelLabel ?? LEAVE_WORDS.cancelLabel}
                variant="destructive"
                onConfirm={() => asking?.proceed()} />
        </LeaveGuardContext.Provider>
    );
});

/** Registers a part of the page whose changes are unsaved while `dirty` holds. */
export function useLeaveGuard(dirty: boolean): void {
    const { setDirty } = useContext(LeaveGuardContext);
    const id = useId();
    useEffect(() => {
        setDirty(id, dirty);
        return () => setDirty(id, false);
    }, [id, dirty, setDirty]);
}

/** A page's own ways out and its own discards: `whenLeaving` asks only while something is unsaved. */
export function useLeaveGuardActions(): Pick<LeaveGuard, "confirmDiscard" | "whenLeaving"> {
    return useContext(LeaveGuardContext);
}

/** A dialog's or sheet's close: at once while `dirty` is false, after the discard confirm while it holds. */
export function useGuardedClose(dirty: boolean, close: () => void): () => void {
    const { confirmDiscard } = useContext(LeaveGuardContext);
    return useCallback(() => {
        if (dirty) {
            confirmDiscard(close, CLOSE_WORDS);
        } else {
            close();
        }
    }, [dirty, close, confirmDiscard]);
}

/** The router for navigation that is not a link (search, Fluid): a push asks first while the page has unsaved changes. */
export function useGuardedRouter(): ReturnType<typeof useRouter> {
    const router = useRouter();
    const { whenLeaving } = useContext(LeaveGuardContext);
    return useMemo(() => ({ ...router, push: (href, options) => whenLeaving(() => router.push(href, options)) }), [router, whenLeaving]);
}
